import { useState } from 'react'
import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ApiFehler, hole, schicke } from '../lib/api'
import { leseTheme, setzeTheme, type Theme } from '../lib/theme'
import { useDarf, useSitzung } from '../session/SessionProvider'
import { Button, Card, ListItem, SegmentedButtons, TextField, useToast } from '../ui'

const THEMES: { wert: Theme; label: string }[] = [
  { wert: 'system', label: 'Gerät' },
  { wert: 'light', label: 'Hell' },
  { wert: 'dark', label: 'Dunkel' },
]

export function Einstellungen() {
  const { abfrage } = useSitzung()
  const me = abfrage.data
  const darf = useDarf('household.manage')
  const zeigeToast = useToast()
  const [name, setName] = useState(me && !me.setupRequired ? me.household.name : '')
  const [zone, setZone] = useState(
    me && !me.setupRequired ? me.household.timezone : 'Europe/Berlin',
  )
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [theme, setTheme] = useState<Theme>(() => leseTheme())
  const googleStatus = new URLSearchParams(window.location.search).get('google')

  const haushalte = useQuery({
    queryKey: ['households'],
    queryFn: () => hole<{ memberId: string; name: string }[]>('/api/households'),
  })

  async function aendern(memberId?: string) {
    setLaeuft(true)
    setFehler(null)
    try {
      await schicke(
        memberId ? 'POST' : 'PATCH',
        memberId ? '/api/households/switch' : '/api/households/current',
        memberId ? { memberId } : { name, timezone: zone },
      )
      zeigeToast('Haushaltseinstellungen gespeichert', 'erfolg')
      window.location.replace('/')
    } catch (e) {
      const msg = e instanceof ApiFehler ? e.message : 'Änderung fehlgeschlagen.'
      setFehler(msg)
      zeigeToast(msg, 'error')
      setLaeuft(false)
    }
  }

  return (
    <>
      <div className="seitenkopf">
        <div>
          <h1>Einstellungen</h1>
          <p>Aussehen, Haushalt und Anmeldung.</p>
        </div>
      </div>

      {fehler ? (
        <p className="fehler" role="alert">
          {fehler}
        </p>
      ) : null}

      <Card titel="Aussehen">
        <p className="unterzeile">
          Famora folgt deinem Gerät — oder bleibt bei der Farbe, die du wählst.
        </p>
        <SegmentedButtons
          label="Erscheinungsbild"
          wert={theme}
          optionen={THEMES}
          onWert={(wahl, ursprung) => {
            setTheme(wahl)
            // Der Kreis wächst dort, wo geklickt wurde - nicht aus der Mitte
            // des Fensters; sonst sähe der Wechsel an jedem Knopf gleich aus.
            setzeTheme(wahl, ursprung)
          }}
        />
      </Card>

      <Card titel="Benachrichtigungen">
        <p className="unterzeile">
          Web-Push-Mitteilungen für Erinnerungen und Aufgaben verwalten.
        </p>
        <div className="reihe">
          <Link className="btn btn--outlined" to="/benachrichtigungen">
            <span className="btn__text">Benachrichtigungen verwalten</span>
          </Link>
        </div>
      </Card>

      {darf && me && !me.setupRequired && !me.preview ? (
        <Card titel="Haushalt">
          <form
            className="stack-4"
            onSubmit={(e) => {
              e.preventDefault()
              void aendern()
            }}
          >
            <TextField
              id="haushalt-name"
              label="Haushaltsname"
              wert={name}
              onWert={setName}
              maxLength={80}
              required
            />
            <TextField
              id="haushalt-zone"
              label="Zeitzone"
              wert={zone}
              onWert={setZone}
              hilfe="Zum Beispiel Europe/Berlin"
              required
            />
            <div className="reihe">
              <Button type="submit" laeuft={laeuft}>
                {laeuft ? 'Wird gespeichert …' : 'Speichern'}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <Card titel="Haushalt wechseln">
        {haushalte.isError ? (
          <p className="fehler" role="alert">
            Haushalte konnten nicht geladen werden.
          </p>
        ) : null}
        <ul className="liste liste--gruppe">
          {(haushalte.data ?? []).map((h) => {
            const aktuell = me && !me.setupRequired && me.member.id === h.memberId
            return (
              <ListItem
                key={h.memberId}
                titel={h.name}
                unterzeile={aktuell ? 'Aktueller Haushalt' : undefined}
                folgend={
                  aktuell ? undefined : (
                    <Button
                      variant="text"
                      disabled={laeuft}
                      onClick={() => {
                        void aendern(h.memberId)
                      }}
                    >
                      Wechseln
                    </Button>
                  )
                }
              />
            )
          })}
        </ul>
      </Card>

      {me && !me.setupRequired && !me.preview && ['owner', 'adult'].includes(me.member.role) ? (
        <Card titel="Google-Konto verbinden">
          {googleStatus === 'verbunden' ? (
            <p role="status">Google-Konto wurde erfolgreich verbunden.</p>
          ) : null}
          {googleStatus === 'konflikt' ? (
            <p className="fehler" role="alert">
              Dieses Google-Konto besitzt einen weiteren Anmeldeweg und kann deshalb nicht
              automatisch zusammengeführt werden. Deine bestehenden Zugänge wurden nicht verändert.
            </p>
          ) : null}
          <p className="unterzeile">
            Verbinde dein Google-Konto mit diesem bestehenden Zugang, damit dein Haushalt erhalten
            bleibt. Melde dich dafür zuerst mit deinem bisherigen Code an. Eine leere, zuvor
            abgebrochene Google-Registrierung und konfliktfreie weitere Haushalte werden automatisch
            übernommen. Doppelte Personenprofile im selben Haushalt werden samt Aufgaben und
            Terminen auf dieses Profil zusammengeführt.
          </p>
          <div className="reihe">
            <a className="btn btn--outlined" href="/api/auth/google/link">
              <span className="btn__text">Google-Konto verbinden</span>
            </a>
          </div>
        </Card>
      ) : null}
    </>
  )
}
